import { useRelativeTime } from '@/hooks/analytics/useRelativeTime'
import { useTvlBreakdowns } from '@/hooks/analytics/useTvlBreakdowns'
import { useVaultsQuery } from '@/queries/vaults.query'
import type { Vault } from '@/types/vault'
import { formatUSD } from '@vaultsfyi/common/utils'
import {
  DialogBody,
  DialogContentWrapper,
  DialogDescription,
  DialogHeader,
  DialogHeaderWrapper,
  DialogScrollArea,
  DialogTitle,
} from '../common/dialog'
import { Loader } from '../common/loader'
import { Separator } from '../common/separator'
import { StatisticsTile } from '../common/statisticsTile'
import { TvlBreakdownChart } from './tvlBreakdownChart'
import { TvlBreakdownLegend } from './tvlBreakdownLegend'
import { TABS } from './tvlSection'

interface Props {
  initialData?: Vault[] | null
}

export function TvlFullView({ initialData }: Props) {
  const { data: vaults } = useVaultsQuery({ initialData: initialData ?? undefined })
  const breakdowns = useTvlBreakdowns(vaults)
  const relativeTime = useRelativeTime(breakdowns?.lastUpdateTimestamp ?? null)

  if (!breakdowns) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader />
      </div>
    )
  }

  return (
    <DialogBody>
      <DialogHeaderWrapper className="pt-6 md:pt-8">
        <DialogHeader>
          <DialogTitle>Total Value Locked</DialogTitle>
          <DialogDescription>
            Capital deposited across all vaults
            {relativeTime !== null && ` · Updated ${relativeTime}`}
          </DialogDescription>
        </DialogHeader>
        <Separator />
      </DialogHeaderWrapper>
      <DialogScrollArea>
        <DialogContentWrapper className="pt-4 md:pt-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatisticsTile label="Vaults" value={breakdowns.vaultCount.toLocaleString()} className="bg-page" />
            <StatisticsTile label="Protocols" value={breakdowns.protocolCount} className="bg-page" />
            <StatisticsTile label="Curators" value={breakdowns.curatorCount} className="bg-page" />
            <StatisticsTile label="Total TVL" value={formatUSD(breakdowns.total)} className="bg-page" />
          </div>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {TABS.map(({ key, label }) => (
              <div key={key} className="flex flex-col gap-5 rounded-[20px] border border-border bg-page p-5">
                <p className="font-semibold text-heading-foreground text-lg">TVL breakdown by {label}</p>
                <div className="flex flex-wrap items-start justify-around gap-4">
                  <TvlBreakdownLegend items={breakdowns[key]} className="max-w-64" />
                  <TvlBreakdownChart items={breakdowns[key]} />
                </div>
              </div>
            ))}
          </div>
        </DialogContentWrapper>
      </DialogScrollArea>
    </DialogBody>
  )
}
